import React, { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import "./css/adminStyles.css";

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    if (!url.startsWith("data:")) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });

const getCroppedCanvas = async (imageSrc, pixelCrop, rotation, flipH, flipV) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  const rotRad = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(rotRad));
  const cos = Math.abs(Math.cos(rotRad));

  const cropW = pixelCrop.width;
  const cropH = pixelCrop.height;

  canvas.width = cropW * cos + cropH * sin;
  canvas.height = cropW * sin + cropH * cos;

  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate(rotRad);
  ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
  ctx.translate(-cropW / 2, -cropH / 2);

  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, pixelCrop.width, pixelCrop.height
  );

  return canvas;
};

function AdminLogoEditor({ currentImage, logoHistory, logoBin, onSave, onDeleteHistory, onRestoreFromBin, onPermanentDelete, onClose, accept = "image/*" }) {
  const initialImage = currentImage != null && currentImage !== "" ? currentImage : (logoHistory && logoHistory.length > 0 ? logoHistory[0] : null);
  const [imageSrc, setImageSrc] = useState(initialImage);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [showBin, setShowBin] = useState(false);
  const [expandedImage, setExpandedImage] = useState(null);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setCroppedAreaPixels(null);
      setShowGallery(false);
      setShowBin(false);
      setExpandedImage(null);
      setRotation(0);
      setFlipH(false);
      setFlipV(false);
    };
    reader.readAsDataURL(file);
  };

  const handleRotate = (deg) => {
    setRotation((prev) => (prev + deg) % 360);
  };

  const handleChooseHistory = (src) => {
    setImageSrc(src);
    setShowGallery(false);
    setShowBin(false);
    setExpandedImage(null);
    setRotation(0);
    setFlipH(false);
    setFlipV(false);
  };

  const isGif = (src) =>
    src ? src.startsWith("data:image/gif") || /\.gif(\?|#|$)/i.test(src) : false;

  const handleSave = async () => {
    if (!imageSrc) return;
    if (isGif(imageSrc)) {
      if (imageSrc.startsWith("data:")) {
        onSave(imageSrc);
        return;
      }
      const resp = await fetch(imageSrc);
      const blob = await resp.blob();
      const reader = new FileReader();
      reader.onload = () => onSave(reader.result);
      reader.readAsDataURL(blob);
      return;
    }
    setProcessing(true);
    try {
      let cropPx = croppedAreaPixels;
      if (!cropPx) {
        const img = await createImage(imageSrc);
        cropPx = { x: 0, y: 0, width: img.naturalWidth, height: img.naturalHeight };
      }
      const canvas = await getCroppedCanvas(imageSrc, cropPx, rotation, flipH, flipV);
      const srcMime = imageSrc.startsWith("data:") ? imageSrc.split(";")[0].split(":")[1] : "image/png";
      const outMime = accept === "image/png" ? "image/png" : accept === "image/gif" ? "image/gif" : srcMime === "image/png" ? "image/png" : "image/webp";
      let output = canvas.toDataURL(outMime, outMime === "image/webp" ? 0.85 : undefined);
      if (outMime === "image/webp" && output.indexOf("image/webp") === -1) {
        output = canvas.toDataURL("image/png");
      }
      onSave(output);
    } catch (err) {
      console.error("Logo crop error:", err);
      onSave(imageSrc);
    } finally {
      setProcessing(false);
    }
  };

  const history = logoHistory || [];
  const bin = logoBin || [];

  const renderGalleryView = () => (
    <div className="AdminLogoGallery">
      <div className="AdminLogoGalleryHeader">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button className="AdminIconBtn" onClick={() => setShowGallery(false)} title="Back" style={{ fontSize: 16, color: "#1B2A4A" }}>&larr;</button>
          <h4 style={{ margin: 0, color: "#1B2A4A", fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: 15 }}>Previous Logos</h4>
        </div>
        {bin.length > 0 && (
          <button className="AdminIconBtn" onClick={() => { setShowBin(true); setExpandedImage(null); }} title="Bin">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
            </svg>
          </button>
        )}
      </div>
      {history.length === 0 ? (
        <div className="AdminLogoGalleryEmpty">No previous images</div>
      ) : (
        <div className="AdminLogoGalleryGrid">
          {history.map((src, i) => (
            <div key={i} className="AdminLogoGalleryItem" onClick={() => setExpandedImage({ src, index: i, source: "history" })}>
              <img src={src} alt={`Previous logo ${i + 1}`} />
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderBinView = () => (
    <div className="AdminLogoGallery">
      <div className="AdminLogoGalleryHeader">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button className="AdminIconBtn" onClick={() => setShowBin(false)} title="Back" style={{ fontSize: 16, color: "#1B2A4A" }}>&larr;</button>
          <h4 style={{ margin: 0, color: "#1B2A4A", fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: 15 }}>Bin</h4>
        </div>
      </div>
      {bin.length === 0 ? (
        <div className="AdminLogoGalleryEmpty">Bin is empty</div>
      ) : (
        <div className="AdminLogoGalleryGrid">
          {bin.map((src, i) => (
            <div key={i} className="AdminLogoGalleryItem" onClick={() => setExpandedImage({ src, index: i, source: "bin" })}>
              <img src={src} alt={`Deleted logo ${i + 1}`} />
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderExpandedView = () => {
    const { src, index, source } = expandedImage;
    const isBin = source === "bin";
    return (
      <div className="AdminLogoExpanded">
        <div className="AdminLogoExpandedHeader">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button className="AdminIconBtn" onClick={() => setExpandedImage(null)} title="Back" style={{ fontSize: 16, color: "#1B2A4A" }}>&larr;</button>
            <h4 style={{ margin: 0, color: "#1B2A4A", fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: 15 }}>
              {isBin ? "Deleted Logo" : "Logo"}
            </h4>
          </div>
        </div>
        <div className="AdminLogoExpandedImageWrap">
          <img src={src} alt={`Logo ${index + 1}`} className="AdminLogoExpandedImage" />
        </div>
        <div className="AdminLogoExpandedActions">
          {isBin ? (
            <>
              <button className="AdminButton AdminButtonSmall AdminButtonSecondary" onClick={() => setExpandedImage(null)} style={{ width: "auto" }}>
                Close
              </button>
              <button className="AdminButton AdminButtonSmall" onClick={() => { onRestoreFromBin(index); setExpandedImage(null); }} style={{ width: "auto" }}>
                Restore to Gallery
              </button>
              <button className="AdminButton AdminButtonSmall AdminButtonDanger" onClick={() => { onPermanentDelete(index); setExpandedImage(null); }} style={{ width: "auto" }}>
                Permanent Delete
              </button>
            </>
          ) : (
            <>
              <button className="AdminButton AdminButtonSmall AdminButtonDanger" onClick={() => { onDeleteHistory(index); setExpandedImage(null); }} style={{ width: "auto" }}>
                Delete
              </button>
              <button className="AdminButton AdminButtonSmall" onClick={() => handleChooseHistory(src)} style={{ width: "auto" }}>
                Choose
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="AdminModalOverlay" onClick={onClose}>
      <div className="AdminLogoEditor" onClick={(e) => e.stopPropagation()}>
        <div className="AdminLogoEditorHeader">
          <h3 className="AdminFooterH3">Edit</h3>
          <button className="AdminIconBtn" onClick={onClose} title="Close" style={{ fontSize: 20, color: "#64748b" }}>&#10005;</button>
        </div>

        {!imageSrc ? (
          <div className="AdminLogoEditorUpload">
            <label className="AdminButton AdminButtonSecondary" style={{ cursor: "pointer", width: "auto", padding: "10px 24px" }}>
              Choose Image
              <input type="file" accept={accept} onChange={handleFileChange} style={{ display: "none" }} />
            </label>
          </div>
        ) : showBin && expandedImage ? (
          renderExpandedView()
        ) : showBin ? (
          renderBinView()
        ) : showGallery && expandedImage ? (
          renderExpandedView()
        ) : showGallery ? (
          renderGalleryView()
        ) : (
          <>
            {isGif(imageSrc) ? (
              <div className="AdminLogoEditorGifPreview">
                <img src={imageSrc} alt="GIF preview" />
              </div>
            ) : (
              <div className="AdminLogoEditorCropArea">
                <div className="AdminLogoCropSide AdminLogoCropSideLeft">
                  <button className={`AdminLogoIconBtn${flipH ? " active" : ""}`} onClick={() => setFlipH((p) => !p)} title={flipH ? "Unflip horizontal" : "Flip horizontal"}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 12h18" /><polyline points="7 8 3 12 7 16" /><polyline points="17 8 21 12 17 16" />
                    </svg>
                  </button>
                  <button className={`AdminLogoIconBtn${flipV ? " active" : ""}`} onClick={() => setFlipV((p) => !p)} title={flipV ? "Unflip vertical" : "Flip vertical"}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 3v18" /><polyline points="8 7 12 3 16 7" /><polyline points="8 17 12 21 16 17" />
                    </svg>
                  </button>
                </div>
                <div className="AdminLogoCropSide AdminLogoCropSideRight">
                  <button className="AdminLogoIconBtn" onClick={() => handleRotate(-90)} title="Rotate left">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                    </svg>
                  </button>
                  <button className="AdminLogoIconBtn" onClick={() => handleRotate(90)} title="Rotate right">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                    </svg>
                  </button>
                </div>
                <div className="AdminLogoEditorImageWrap">
                  <img src={imageSrc} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", transform: `scale(${flipH ? -1 : 1}, ${flipV ? -1 : 1}) rotate(${rotation}deg)` }} />
                </div>
              </div>
            )}

            <div className="AdminLogoEditorControls">
              <label className="AdminButton AdminButtonSmall AdminButtonSecondary" style={{ cursor: "pointer", width: "auto" }}>
                Change
                <input type="file" accept={accept} onChange={handleFileChange} style={{ display: "none" }} />
              </label>

              {history.length > 0 && (
                <button className="AdminButton AdminButtonSmall AdminButtonSecondary" onClick={() => setShowGallery(true)} style={{ width: "auto" }}>
                  Previous
                </button>
              )}
            </div>
          </>
        )}

        <div className="AdminLogoEditorActions">
          {!showGallery && !showBin && (
            <button
              className="AdminButton AdminButtonSmall"
              onClick={handleSave}
              disabled={!imageSrc || processing}
              style={{ width: "auto" }}
            >
              {processing ? "Saving..." : "Save"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminLogoEditor;
